import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Copy, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RelativeTime } from './relative-time';
import { copyToClipboard } from '@/lib/format';
import { useIsMobile } from '@/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import type { BlacklistEntry } from '@/schemas/blacklist';

interface BlacklistTableProps {
  data: BlacklistEntry[];
  total: number;
  isLoading: boolean;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onRemove: (entry: BlacklistEntry) => void;
}

export default function BlacklistTable({
  data,
  total,
  isLoading,
  page,
  limit,
  onPageChange,
  onLimitChange,
  onRemove,
}: BlacklistTableProps) {
  const { t } = useTranslation();

  const columns: ColumnDef<BlacklistEntry>[] = [
    {
      accessorKey: 'serial',
      header: t('blacklist.table.serial'),
      cell: ({ row }) => {
        const serial = row.original.serial;
        const licenseId = row.original.licenseId;
        return (
          <div className="group flex items-center gap-2">
            {licenseId ? (
              <Link
                to={`/licenses/${licenseId}`}
                className="rounded-sm font-mono text-[13.5px] font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {serial}
              </Link>
            ) : (
              <span className="font-mono text-[13.5px] font-medium text-foreground">{serial}</span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
              onClick={() => copyToClipboard(serial)}
              aria-label={t('licenses.table.copySerialAria', { serial })}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'reason',
      header: t('blacklist.table.reason'),
      cell: ({ row }) => {
        const reason = row.original.reason;
        const truncated = reason.length > 60 ? `${reason.slice(0, 60)}…` : reason;
        return (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="text-[13.5px] text-muted-foreground"
                  aria-describedby={`reason-${row.original.id}`}
                >
                  {truncated}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p id={`reason-${row.original.id}`} className="max-w-xs text-xs">
                  {reason}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('blacklist.table.created'),
      cell: ({ row }) => (
        <span className="text-[13.5px] text-muted-foreground">
          <RelativeTime date={row.original.createdAt} />
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('blacklist.table.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-[13px] text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(row.original)}
            aria-label={t('blacklist.table.removeAria', { serial: row.original.serial })}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t('blacklist.table.remove')}
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(total / limit) || 1,
  });

  const pages = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <div className="space-y-2 pt-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  const pagination =
    total > 0 ? (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <span className="text-[13px] tabular-nums text-faint">
          {t('common.showingRange', { start, end, total })}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-input bg-transparent"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label={t('common.previousPage')}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[64px] text-center text-[13px] tabular-nums text-muted-foreground">
            {page} / {pages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-input bg-transparent"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
            aria-label={t('common.nextPage')}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Select
            value={String(limit)}
            onValueChange={(v) => {
              onLimitChange(parseInt(v, 10));
              onPageChange(1);
            }}
          >
            <SelectTrigger
              className="h-8 w-[72px] border-input bg-transparent text-[13px]"
              aria-label={t('common.pageSize')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    ) : null;

  if (isMobile) {
    return (
      <div>
        <ul className="border-t">
          {data.map((entry) => (
            <li key={entry.id} className="border-b px-1 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {entry.licenseId ? (
                    <Link
                      to={`/licenses/${entry.licenseId}`}
                      className="rounded-sm font-mono text-[13.5px] font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {entry.serial}
                    </Link>
                  ) : (
                    <span className="font-mono text-[13.5px] font-medium text-foreground">
                      {entry.serial}
                    </span>
                  )}
                  <p className="mt-1.5 text-[13px] leading-snug text-muted-foreground">
                    {entry.reason}
                  </p>
                  <p className="mt-1.5 text-[13px] text-faint">
                    <RelativeTime date={entry.createdAt} />
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => onRemove(entry)}
                  aria-label={t('blacklist.table.removeAria', { serial: entry.serial })}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {pagination}
      </div>
    );
  }

  return (
    <div>
      <Table className="min-w-[560px] md:min-w-0">
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="hover:bg-transparent">
              {hg.headers.map((header) => (
                <TableHead key={header.id} className="h-11 px-3 text-[13px] font-medium text-faint">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="hover:bg-foreground/5">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="px-3 py-3.5">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {t('blacklist.empty')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {pagination}
    </div>
  );
}
